import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CityProvider } from './context/CityContext';
import { cmsApi } from './api';
import Nav from './components/Nav';
import Footer from './components/Footer';
import ZenGuide from './components/ZenGuide';
import MaintenancePage from './pages/MaintenancePage'; // eager — needed before settings load
import { SiteSettingsContext, useSiteSettings } from './context/SiteSettingsContext';

// ── Lazy-load every page so each route is a separate JS chunk.
// The initial bundle only ships Nav + Footer + shared context — everything else
// is fetched on first navigation to that route (and cached by the browser thereafter).
const Home               = lazy(() => import('./pages/Home'));
const Places             = lazy(() => import('./pages/Places'));
const Events             = lazy(() => import('./pages/Events'));
const Activities         = lazy(() => import('./pages/Activities'));
const Communities        = lazy(() => import('./pages/CommunitiesPage'));
const PlaceDetail        = lazy(() => import('./pages/PlaceDetail'));
const CommunityDetail    = lazy(() => import('./pages/CommunityDetailPage'));
const EventActivityDetail= lazy(() => import('./pages/EventActivityDetail'));
const Search             = lazy(() => import('./pages/Search'));
const Login              = lazy(() => import('./pages/Login'));
const Contact            = lazy(() => import('./pages/Contact'));
const Cities             = lazy(() => import('./pages/Cities'));
const Profile            = lazy(() => import('./pages/Profile'));
const Privacy            = lazy(() => import('./pages/Privacy'));
const Terms              = lazy(() => import('./pages/Terms'));
const Blog               = lazy(() => import('./pages/Blog'));
const BlogPost           = lazy(() => import('./pages/BlogPost'));
// ITINERARY_DISABLED — const Plan = lazy(() => import('./pages/Plan'));
// ITINERARY_DISABLED — const Itinerary = lazy(() => import('./pages/Itinerary'));

// Minimal spinner shown while a lazy page chunk is downloading
function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        border: '3px solid #e0e0e0',
        borderTopColor: 'var(--gd, #1a7a4a)',
        animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Redirects to /login with the current path saved so we return after sign-in
function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  return children;
}

function AppRoutes() {
  const { blogEnabled } = useSiteSettings();
  return (
    <>
      <ScrollTop />
      <Nav />
      <main>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/"               element={<Home />} />
          <Route path="/search"         element={<Search />} />
          <Route path="/cities"         element={<Cities />} />
          <Route path="/contact"        element={<Contact />} />
          <Route path="/login"          element={<Login />} />

          {/* Places */}
          <Route path="/places"           element={<Places />} />
          <Route path="/places/:slug"     element={<PlaceDetail />} />

          {/* Events */}
          <Route path="/events"           element={<Events />} />
          <Route path="/events/:slug"     element={<EventActivityDetail type="event" />} />

          {/* Activities */}
          <Route path="/activities"       element={<Activities />} />
          <Route path="/activities/:slug" element={<EventActivityDetail type="activity" />} />

          {/* Communities */}
          <Route path="/communities"       element={<Communities />} />
          <Route path="/communities/:slug" element={<CommunityDetail />} />

          {/* ITINERARY_DISABLED
          <Route path="/itinerary/:slug"  element={<Itinerary />} />
          <Route path="/plan"           element={<Plan />} />
          */}

          {/* Profile — requires login */}
          <Route path="/profile"        element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />

          {/* Redirect aliases */}
          <Route path="/restaurants"    element={<Navigate to="/places?cat=Restaurant" replace />} />
          <Route path="/nightlife"      element={<Navigate to="/places?cat=Nightclub" replace />} />
          <Route path="/hotels"         element={<Navigate to="/places?cat=Hotel" replace />} />
          <Route path="/cafes"          element={<Navigate to="/places?cat=Cafe" replace />} />

          {/* Blog — only rendered when blog_enabled in site_settings */}
          <Route path="/blog"       element={blogEnabled ? <Blog />     : <Navigate to="/" replace />} />
          <Route path="/blog/:slug" element={blogEnabled ? <BlogPost /> : <Navigate to="/" replace />} />

          {/* Legal */}
          <Route path="/privacy"        element={<Privacy />} />
          <Route path="/terms"          element={<Terms />} />

          {/* 404 */}
          <Route path="*"               element={<NotFound />} />
        </Routes>
        </Suspense>
      </main>
      <Footer />
      <ZenGuide />
    </>
  );
}

const BYPASS_KEY      = 'zende_preview_bypass';
const BYPASS_TOKEN    = 'zende';   // visit ?preview=zende to bypass maintenance mode
const SETTINGS_CACHE  = 'zende_settings';

export default function App() {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');

  // Use cached settings for instant render on repeat visits
  const cached = (() => {
    try { return JSON.parse(sessionStorage.getItem(SETTINGS_CACHE) || 'null'); } catch { return null; }
  })();

  const [maintenance, setMaintenance] = useState(
    cached?.maintenance_mode === '1' || cached?.maintenance_mode === 1
  );
  const [blogEnabled, setBlogEnabled] = useState(
    cached ? cached.blog_enabled !== '0' : true
  );
  // If we have cached settings we can render immediately; otherwise wait for the fetch
  const [settingsLoaded, setSettingsLoaded] = useState(!!cached);

  useEffect(() => {
    // Check URL for bypass token — store it so it survives page navigation
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('preview') === BYPASS_TOKEN) {
      sessionStorage.setItem(BYPASS_KEY, '1');
    }

    cmsApi.getSiteSettings()
      .then(s => {
        sessionStorage.setItem(SETTINGS_CACHE, JSON.stringify(s));
        setMaintenance(s.maintenance_mode === '1' || s.maintenance_mode === 1);
        setBlogEnabled(s.blog_enabled !== '0');

      })
      .catch(() => {/* fail open — show the app */})
      .finally(() => setSettingsLoaded(true));
  }, []);

  // Don't render anything until we know the maintenance state
  if (!settingsLoaded) return null;

  // Admin bypass: visiting ?preview=zende grants full access for the session
  const hasBypass = sessionStorage.getItem(BYPASS_KEY) === '1';
  if (maintenance && !hasBypass) return <MaintenancePage />;

  return (
    <SiteSettingsContext.Provider value={{ blogEnabled }}>
      <AuthProvider>
        <CityProvider>
          <BrowserRouter basename={base}>
            <AppRoutes />
          </BrowserRouter>
        </CityProvider>
      </AuthProvider>
    </SiteSettingsContext.Provider>
  );
}

function NotFound() {
  return (
    <div style={{ padding:'120px 28px 80px', textAlign:'center', minHeight:'60vh' }}>
      <div style={{ fontSize:60, marginBottom:16 }}>🗺</div>
      <h1 style={{ fontFamily:'var(--ff)', fontSize:42, fontStyle:'italic', color:'var(--gd)', marginBottom:8 }}>
        Lost in Abuja
      </h1>
      <p style={{ color:'var(--muted)', marginBottom:24 }}>This page doesn't exist.</p>
      <a href="/" style={{ background:'var(--gd)', color:'#fff', padding:'10px 24px', borderRadius:22, fontSize:14, fontWeight:600, textDecoration:'none' }}>
        Go home
      </a>
    </div>
  );
}
